import React, { FC, useEffect, useRef, useState } from 'react';
import { apiService } from 'utils/api';
import './styles.scss';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  DateRangeSelector,
  ENUM_DATE_RANGE,
  getDateRangeSelector
} from '@cokitchen/cokitchen-components';
import { dateDifference, formatTime } from 'utils/functions';
import { ALL_POLYGONS_ID } from 'utils/constants';
import moment from 'moment';
import PolygonSelector from 'components/base/polygon-selector';
import useDashboardContext from 'contexts/dashboard.context';

const OrderChart: FC = () => {
  const { activeCokitchen } = useDashboardContext();
  ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

  const didMount = useRef(false);
  const [chartData, setChartData] = useState([] as any);
  const [filters, setFilters] = useState({
    polygonId: '',
    dateRange: getDateRangeSelector(ENUM_DATE_RANGE.THIS_WEEK)
  });

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const
      }
    },

    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
          drawBorder: false
        }
      },
      y: {
        stacked: true,
        border: { dash: [4, 4] },
        grid: {
          display: true,
          drawBorder: false
        }
      }
    }
  };

  useEffect(() => {
    if (activeCokitchen?.id) setFilters({ ...filters, polygonId: '' });
  }, [activeCokitchen]);

  useEffect(() => {
    if (didMount.current) getChartData();
    didMount.current = true;
  }, [filters]);

  const getChartData = async () => {
    const {
      polygonId,
      dateRange: { startDate: start_time, endDate: end_time }
    } = filters;

    let interval = '';

    if (dateDifference(start_time, end_time) <= 1) {
      interval = 'hour';
    } else if (dateDifference(start_time, end_time) > 56) {
      interval = 'month';
    } else if (dateDifference(start_time, end_time) > 1) {
      interval = 'day';
    }
    const params: any = {
      start_date: start_time,
      end_date: end_time,
      interval,
      cokitchen_id: activeCokitchen?.id
    };
    if (polygonId !== ALL_POLYGONS_ID && polygonId) params.cokitchen_polygon_id = polygonId;

    const res = await apiService.get('logistics/report/order-count', {
      params,
      baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
    });

    const result = res.data?.data?.results ?? [];
    const pickupOrders = result?.map((item: any) => {
      return item.pickup;
    });
    const deliveryOrders = result?.map((item: any) => {
      return item.delivery;
    });
    const labelsFormat = result?.map((item: any) => new Date(item.time));
    const labels = labelsFormat.map((item: any) => {
      return `${moment(item).format('DD/MM')} ${interval === 'hour' ? formatTime(item) : ''}`;
    });
    setChartData({
      labels,
      datasets: [
        {
          label: 'Delivery Orders',
          backgroundColor: '#3FB6FD',
          data: deliveryOrders
        },
        {
          label: 'Pick-up Orders',
          backgroundColor: '#F73B58',
          data: pickupOrders
        }
      ]
    });
  };

  return (
    <div className='border rounded-lg border-[#DEDEDE] py-6 mb-4'>
      <div className='flex flex-col lg:flex-row gap-4 justify-between mb-5 mx-6'>
        <p className='font-medium text-lg'>Order Chart</p>
        <div className='flex gap-2 grow lg:grow-0 justify-end'>
          <PolygonSelector
            hasAll
            polygonId={filters.polygonId}
            setPolygonId={(polygonId) => setFilters({ ...filters, polygonId })}
          />
          <DateRangeSelector
            onChange={(v) => setFilters({ ...filters, dateRange: v.date })}
            showTime
          />
        </div>
      </div>
      <hr className='text-[#DCE2EA] mb-5' />
      <div>
        {chartData.length !== 0 ? (
          <Bar options={options} data={chartData} height={400} />
        ) : (
          <p className='font-semibold text-center my-16'>Loading ...</p>
        )}
      </div>
    </div>
  );
};

export default OrderChart;
